import React, { useRef, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_DIMENSION = 1920;       // longest side
const TARGET_QUALITY = 0.85;      // JPEG quality after resize
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // hard ceiling (matches server)

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

// Read a File into an HTMLImageElement via a data URL — works for HEIC on iOS Safari
// (Safari decodes HEIC natively when drawn to canvas).
function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read file"));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Couldn't decode image — try saving as JPEG first"));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// Resize the image to fit MAX_DIMENSION and re-encode as JPEG. Returns a Blob.
function resizeImage(img) {
  const ratio = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error("Couldn't encode image"));
        else resolve(blob);
      },
      "image/jpeg",
      TARGET_QUALITY,
    );
  });
}

export default function ImageUploader({ value, onChange, testId = "image-uploader" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") && !/\.(heic|heif)$/i.test(file.name)) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error("That file is huge — please pick something under 15 MB");
      return;
    }
    setBusy(true);
    try {
      // Always re-encode to JPEG <= 1920px so iPhone HEIC + huge photos just work.
      const img = await fileToImage(file);
      const blob = await resizeImage(img);
      const fd = new FormData();
      const baseName = file.name.replace(/\.[^.]+$/, "") || "upload";
      fd.append("file", blob, `${baseName}.jpg`);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    }
    setBusy(false);
  };

  const previewUrl = resolveImageUrl(value);

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-start gap-4">
        <div className="w-24 h-24 border border-white/15 bg-black flex items-center justify-center overflow-hidden flex-shrink-0">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] uppercase text-gray-600 tracking-widest">No image</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <button
            type="button"
            data-testid={`${testId}-pick`}
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 border border-white/20 hover:border-[var(--hk-orange)] hover:text-[var(--hk-orange)] px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "Uploading..." : "Upload image"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.heic,.heif"
            className="hidden"
            data-testid={`${testId}-input`}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-2">
            iPhone photos OK · auto-resized
          </p>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="...or paste an image URL"
            className="w-full mt-3 bg-transparent border-b border-white/15 focus:border-[var(--hk-purple)] outline-none py-1.5 text-xs text-white placeholder-gray-600"
            data-testid={`${testId}-url-input`}
          />
        </div>
      </div>
    </div>
  );
}

export { resolveImageUrl };
